<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion
      v-if="isReady && userStore.role && filteredRoutes.length > 0"
      :key="`accordion-${userStore.role}`"
      multiple
    >
      <VaCollapse v-for="route in filteredRoutes" :key="`collapse-${route.name}`" stateful>
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children && route.children.length > 0 ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children && route.children.length > 0 ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta && route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon
                  v-if="route.children && route.children.length > 0"
                  :name="arrowDirection(isCollapsed)"
                  size="20px"
                />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-if="route.children && route.children.length > 0">
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <VaSidebarItem
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(route.displayName)}`"
                hover-opacity="0.10"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                  <VaSidebarItemTitle class="leading-5 font-semibold">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useUserStore } from '../../stores/user-store'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const userStore = useUserStore()
    const isReady = ref(false)

    onMounted(async () => {
      // Si le rôle n'est pas chargé, essayer de le charger
      if (!userStore.role && !userStore.isLoading) {
        await userStore.loadUserData()
      }

      // Attendre le prochain cycle de rendu pour s'assurer que tout est stable
      await nextTick()

      // Petit délai supplémentaire pour éviter les problèmes d'initialisation de l'accordion
      setTimeout(() => {
        isReady.value = true
      }, 50)
    })
    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    // Function to check if user has access to a route based on their role
    const hasAccess = (routeItem: INavigationRoute): boolean => {
      // If no allowedRoles specified, route is accessible to all
      if (!routeItem.allowedRoles || routeItem.allowedRoles.length === 0) {
        return true
      }

      // If user role is not loaded yet, show all routes to avoid blank screen
      if (!userStore.role) {
        return true
      }

      // Check if user's role is in the allowed roles list
      const hasPermission = routeItem.allowedRoles.includes(userStore.role)

      return hasPermission
    }

    // Filter routes based on user's role
    const filteredRoutes = computed(() => {
      return (
        navigationRoutes.routes
          .filter((route) => hasAccess(route))
          .map((route) => {
            // If route has children, filter them as well
            if (route.children) {
              const filteredChildren = route.children.filter((child) => hasAccess(child))
              return {
                ...route,
                children: filteredChildren,
              }
            }
            return route
          })
          // Remove parent routes that originally had children but have none after filtering
          .filter((route) => {
            // If route originally had children but has none after filtering, remove it
            if (route.children !== undefined && route.children.length === 0) {
              return false
            }
            return true
          })
      )
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children || section.children.length === 0) {
        return route.name === section.name
      }

      return section.children.some(({ name }) => route.name === name)
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    return {
      writableVisible,
      sidebarWidth,
      color,
      activeColor,
      filteredRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      isReady,
      userStore,
    }
  },
})
</script>
